import axios, { CancelToken, CancelTokenSource } from "axios";
import { emailBuilderAutosaveDueAt } from "pages/secure/facility/marketing/emailBuilder/shell/emailBuilderDraftStatusLabel";
import {
  EMAIL_BUILDER_AUTOSAVE_DEBOUNCE_MS,
  EMAIL_BUILDER_HTTP_OK,
} from "redux/reducers/emailBuilder/emailBuilderAutosave";
import type { TEmailBuilderAutoSaveStatus } from "redux/reducers/emailBuilder/emailBuilder";

export type TEmailBuilderAutoSaveResult = "ok" | "error" | "cancelled";

type TEmailBuilderAutoSaveUpdateStatus = (
  updatedStatus: TEmailBuilderAutoSaveStatus,
  autoSaveDueAt: number | null,
) => void;

type TEmailBuilderAutoSaveRunner = (token: CancelToken, saveGeneration: number) => Promise<number>;

type TEmailBuilderAutoSaveDispatchError = (status: number) => void;

type TEmailBuilderPendingLatestSave = {
  updateStatus: TEmailBuilderAutoSaveUpdateStatus;
  save: TEmailBuilderAutoSaveRunner;
  dispatchError: TEmailBuilderAutoSaveDispatchError;
  waiters: Array<(result: TEmailBuilderAutoSaveResult) => void>;
};

export class EmailBuilderAutoSaver {
  private source: CancelTokenSource | null = null;
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<TEmailBuilderAutoSaveResult> | null = null;
  private inFlightSuperseded = false;
  private pendingLatest: TEmailBuilderPendingLatestSave | null = null;
  private latestSaveGeneration = 0;

  public beginSaveGeneration(): number {
    this.latestSaveGeneration += 1;
    return this.latestSaveGeneration;
  }

  public isCurrentSaveGeneration(saveGeneration: number): boolean {
    return saveGeneration === this.latestSaveGeneration;
  }

  private cancelPendingDebounce(): void {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  public cancelAutoSave() {
    this.source?.cancel();
    this.source = null;
    this.cancelPendingDebounce();
  }

  public autoSave(
    updateStatus: TEmailBuilderAutoSaveUpdateStatus,
    save: TEmailBuilderAutoSaveRunner,
    dispatchError: TEmailBuilderAutoSaveDispatchError,
  ) {
    this.cancelPendingDebounce();

    if (this.inFlight !== null) {
      this.inFlightSuperseded = true;
    } else {
      this.source?.cancel();
      this.source = null;
    }

    updateStatus("unsaved-changes", emailBuilderAutosaveDueAt(Date.now()));

    // A forced latest flush already owns the next PUT; only refresh its handlers.
    if (this.pendingLatest !== null) {
      this.pendingLatest.updateStatus = updateStatus;
      this.pendingLatest.save = save;
      this.pendingLatest.dispatchError = dispatchError;
      return;
    }

    this.source = axios.CancelToken.source();
    const sourceRef = this.source;

    this.timeout = setTimeout(() => {
      this.timeout = null;
      void this.startSave(updateStatus, save, dispatchError, sourceRef);
    }, EMAIL_BUILDER_AUTOSAVE_DEBOUNCE_MS);
  }

  public saveNow(
    updateStatus: TEmailBuilderAutoSaveUpdateStatus,
    save: TEmailBuilderAutoSaveRunner,
    dispatchError: TEmailBuilderAutoSaveDispatchError,
  ): Promise<TEmailBuilderAutoSaveResult> {
    this.cancelPendingDebounce();

    if (this.inFlight !== null && !this.inFlightSuperseded) {
      return this.inFlight;
    }

    if (this.inFlight !== null) {
      return this.queueLatestSave(updateStatus, save, dispatchError);
    }

    this.source = axios.CancelToken.source();
    const sourceRef = this.source;

    return this.trackInFlight(this.runSave(updateStatus, save, dispatchError, sourceRef));
  }

  private startSave(
    updateStatus: TEmailBuilderAutoSaveUpdateStatus,
    save: TEmailBuilderAutoSaveRunner,
    dispatchError: TEmailBuilderAutoSaveDispatchError,
    sourceRef: CancelTokenSource,
  ): Promise<TEmailBuilderAutoSaveResult> {
    if (this.inFlight !== null) {
      this.inFlightSuperseded = true;
      return this.queueLatestSave(updateStatus, save, dispatchError);
    }

    return this.trackInFlight(this.runSave(updateStatus, save, dispatchError, sourceRef));
  }

  private queueLatestSave(
    updateStatus: TEmailBuilderAutoSaveUpdateStatus,
    save: TEmailBuilderAutoSaveRunner,
    dispatchError: TEmailBuilderAutoSaveDispatchError,
  ): Promise<TEmailBuilderAutoSaveResult> {
    if (this.pendingLatest === null) {
      this.pendingLatest = {
        updateStatus,
        save,
        dispatchError,
        waiters: [],
      };
    } else {
      this.pendingLatest.updateStatus = updateStatus;
      this.pendingLatest.save = save;
      this.pendingLatest.dispatchError = dispatchError;
    }

    return new Promise(resolve => {
      this.pendingLatest?.waiters.push(resolve);
    });
  }

  private drainPendingLatest() {
    const pending = this.pendingLatest;
    if (pending === null) {
      return;
    }

    this.pendingLatest = null;
    this.source = axios.CancelToken.source();
    const sourceRef = this.source;
    const next = this.trackInFlight(
      this.runSave(pending.updateStatus, pending.save, pending.dispatchError, sourceRef),
    );

    void next.then(result => {
      for (const waiter of pending.waiters) {
        waiter(result);
      }
    });
  }

  private trackInFlight(promise: Promise<TEmailBuilderAutoSaveResult>): Promise<TEmailBuilderAutoSaveResult> {
    this.inFlight = promise;
    void promise.finally(() => {
      if (this.inFlight !== promise) {
        return;
      }

      this.inFlight = null;
      this.inFlightSuperseded = false;
      this.drainPendingLatest();
    });
    return promise;
  }

  private async runSave(
    updateStatus: TEmailBuilderAutoSaveUpdateStatus,
    save: TEmailBuilderAutoSaveRunner,
    dispatchError: TEmailBuilderAutoSaveDispatchError,
    sourceRef: CancelTokenSource,
  ): Promise<TEmailBuilderAutoSaveResult> {
    updateStatus("saving", null);

    const saveGeneration = this.beginSaveGeneration();
    const status = await save(sourceRef.token, saveGeneration);

    if (status !== EMAIL_BUILDER_HTTP_OK) {
      if (sourceRef.token.reason) {
        if (this.pendingLatest === null) {
          updateStatus("unsaved-changes", null);
        }
        return "cancelled";
      }

      updateStatus("error", null);
      dispatchError(status);
      return "error";
    }

    if (this.isCurrentSaveGeneration(saveGeneration)) {
      updateStatus("saved", null);
    }
    return "ok";
  }
}
